import {
	Accordion,
	Form,
	Grid,
	List,
	Message,
	Tab,
} from '@mairistem/composants';
import {
	type CodeTva,
	type Exercice,
	getLibelleRechercheImputation,
	ImputationSearchInput,
	type LigneMouvement,
	Montants,
	type Mouvement,
	type Sens,
} from '@mairistem/comptabilite';
import { Icon } from '@mairistem/finances-utils';
import { isNil } from 'lodash';
import React from 'react';
import type BaseProfilEcritureMouvement from '../../../../classes/mouvements/profilEcriture/Base/BaseProfilEcritureMouvement';
import TYPE_COMPTA from '../../../../constants/common/typeCompta';
import type PanelOptions from '../../../../interfaces/pieces/mouvements/panelOptions';
import LigneMouvementLiaison from './LigneMouvementLiaison';
import LigneMouvementTiers from './LigneMouvementTiers';
import './LignesPiecesControl.css';

const TEXT_TYPE_COMPTA = {
	HT: 'HT',
	TTC: 'TTC',
} as const;

interface LignesPiecesControlProps {
	accordionActive: boolean;
	codeBHM: string;
	codesTva: CodeTva[];
	exercice: Exercice;
	identifiantEntite: number;
	index: number;
	isInfiniteList: boolean;
	isLigneOpen: boolean;
	ligne: LigneMouvement;
	loadingCodesTva: boolean;
	onChange: (data: Partial<LigneMouvement>) => void;
	onDelete: () => void;
	panelOptions: PanelOptions;
	profilEcriture: BaseProfilEcritureMouvement;
	sens: Sens;
	setModalOpen: (identifiantLigne: number | string) => void;
	setOpenedLignes: React.Dispatch<React.SetStateAction<(number | string)[]>>;
	setPanelOptions: React.Dispatch<React.SetStateAction<PanelOptions>>;
}

const LignesPiecesControl = ({
	accordionActive,
	codeBHM,
	codesTva,
	exercice,
	identifiantEntite,
	index,
	isInfiniteList,
	isLigneOpen,
	ligne,
	loadingCodesTva,
	onChange,
	onDelete,
	panelOptions,
	profilEcriture,
	sens,
	setModalOpen,
	setOpenedLignes,
	setPanelOptions,
}: LignesPiecesControlProps) => {
	const [panelData, setPanelData] = React.useState<
		Partial<LigneMouvement> & Partial<Mouvement> & Partial<PanelOptions>
	>();

	const isAccordionShown = isInfiniteList ? isLigneOpen : accordionActive;

	const handleMontantsChange = ({
		identifiantCodeTVA,
		montantHT,
		montantTTC,
		montantTVA,
	}: {
		identifiantCodeTVA: number;
		montantHT: number;
		montantTTC: number;
		montantTVA: number;
	}) => {
		onChange({
			identifiantCodeTva: identifiantCodeTVA,
			mtht: Number(montantHT.toFixed(2)),
			mttc: Number(montantTTC.toFixed(2)),
			mtva: Number(montantTVA.toFixed(2)),
		});
	};

	const handlePanelChange = (data: Partial<LigneMouvement>) => {
		onChange(data);
	};

	const handleImputationChange = (data) => {
		if (!isNil(data)) {
			onChange({
				identifiantImputation: data.identifiant,
				imputation: data,
			});
		}
	};

	// Permet de construire l'objet contenant toutes les données pour les panels
	React.useEffect(() => {
		setPanelData({
			...ligne,
			...panelOptions,
			codeBHM,
		});
	}, [codeBHM, ligne, panelOptions]);

	const panes = [
		{
			menuItem: "Blocs d'informations",
			render: () => {
				const visiblePanels = profilEcriture?.panels?.filter(
					(panel) => panel.isVisible(panelData) && !panel.isOnlyMouvement,
				);

				return (
					<Tab.Pane className="panelTab">
						{visiblePanels?.length > 0 ? (
							<List className="panelList">
								{visiblePanels.map((panel) => (
									<List.Item className="panelItem" key={panel.code}>
										{panel.getComponent(
											panelData,
											handlePanelChange,
											null,
											sens,
											setPanelOptions,
										)}
									</List.Item>
								))}
							</List>
						) : (
							<Message info content="Aucune information à afficher" />
						)}
					</Tab.Pane>
				);
			},
		},
		profilEcriture?.isLiaisonVisible && {
			menuItem: 'Liaisons',
			render: () => (
				<LigneMouvementLiaison
					ligneEngagement={ligne?.ligneEngagement}
					ligneFacture={ligne?.ligneFacture}
				/>
			),
		},
		{
			menuItem: 'Tiers mémorisé',
			render: () => (
				<LigneMouvementTiers
					ligne={ligne}
					onChange={handlePanelChange}
					profilEcriture={profilEcriture}
					sens={sens}
				/>
			),
		},
	];

	const handleLigneAccordionActive = () => {
		setOpenedLignes((old) =>
			// Si la ligne n'est pas déjà dans la liste
			old.some((id) => id === ligne.identifiant)
				? // On la retire
					old.filter((id) => id !== ligne.identifiant)
				: // sinon on l'ajoute
					[...old, ligne.identifiant],
		);
	};

	return (
		<>
			<Grid id={`ligne-${ligne.identifiant}`}>
				<Grid.Row data-testid={`ligneRow-${index}`}>
					{isInfiniteList && (
						<Grid.Column textAlign="right" verticalAlign="middle" width={1}>
							<Icon
								cursor="pointer"
								data-testid={`ligneRow-toggle-${index}`}
								iconSet="Lucide"
								name={`CircleChevron${isAccordionShown ? 'Up' : 'Down'}`}
								onClick={handleLigneAccordionActive}
							/>
						</Grid.Column>
					)}
					<Grid.Column width={isInfiniteList ? 14 : 15}>
						<Form>
							<Form.Group>
								<Form.Field
									control={ImputationSearchInput}
									identifiantEntite={identifiantEntite}
									annee={exercice?.anneeExercice}
									sens={sens}
									onChange={handleImputationChange}
									defaultValue={{
										libelle:
											getLibelleRechercheImputation(
												ligne?.imputation?.article?.code,
												ligne?.imputation?.operation?.code,
												ligne?.imputation?.fonction?.code,
												ligne?.imputation?.analytique?.code,
												ligne?.imputation?.ventilation?.code,
												ligne?.imputation?.natureBudgetaire,
											) ?? null,
										sens,
										chapitre: ligne?.imputation?.chapitre ?? null,
									}}
									withMenu
									label="Imputation"
									width={8}
									item={{
										action: () => setModalOpen(ligne.identifiant),
										codes: [73],
										libelle: 'Alt+i pour ouvrir le menu',
									}}
									natureBudgetaire={
										ligne?.imputation?.natureBudgetaire ??
										profilEcriture?.imputation?.nature
									}
									articles={profilEcriture?.imputation?.articles}
									chapitre={profilEcriture?.imputation?.chapitre}
									section={profilEcriture?.imputation?.section}
									data-testid={`imputationLigne-${index}`}
									searchComplement={{
										withAnalytique: true,
										withArticle: true,
										withChapitre: true,
										withFonction: true,
										withOperation: true,
										withVentilation: true,
									}}
								/>
								<Montants
									onChange={handleMontantsChange}
									montantHT={ligne?.mtht}
									montantTVA={ligne?.mtva}
									montantTTC={ligne?.mttc}
									loadingCodesTva={loadingCodesTva}
									codesTva={codesTva}
									identifiantCodeTVA={
										ligne?.identifiantCodeTva ?? exercice?.identifiantCodeTva
									}
									// Le montant de référence (celui qui ne bouge pas) dépend
									// uniquement du type de comptabilité de l'exercice : la TVA
									// récupérable (code service TVA) ne change pas le sens de calcul
									typeComptabilite={
										exercice?.typeCompta === TYPE_COMPTA.HT
											? TEXT_TYPE_COMPTA.HT
											: TEXT_TYPE_COMPTA.TTC
									}
									tauxTVAProps={{
										width: 2,
										'data-testid': `tauxTva-${index}`,
									}}
									montantHTProps={{
										width: 3,
										'data-testid': `montantHT-${index}`,
									}}
									montantTTCProps={{
										width: 3,
										'data-testid': `montantTTC-${index}`,
									}}
									montantTVAProps={{
										width: 3,
										'data-testid': `montantTVA-${index}`,
									}}
									inline
								/>
							</Form.Group>
						</Form>
					</Grid.Column>
					<Grid.Column verticalAlign="middle" width={1}>
						<Icon
							className="deleteLigneIcon"
							color="red"
							cursor="pointer"
							data-testid={`supprimeLigne-${index}`}
							iconSet="Lucide"
							name="CircleX"
							onClick={() => onDelete()}
						/>
					</Grid.Column>
				</Grid.Row>
			</Grid>
			{isAccordionShown && (
				<Accordion>
					<Accordion.Content active={isAccordionShown}>
						<Tab menu={{ secondary: true, pointing: true }} panes={panes} />
					</Accordion.Content>
				</Accordion>
			)}
		</>
	);
};

export default LignesPiecesControl;
